import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { map } from "rxjs/operators";
import { AppSettings } from "../../../config/config";
import { apiConstants } from "../../constant/apiConstant";

// Same endpoint paths BroadcastService (admin) calls, but pointed at the
// staff server — same as StaffClassService does for "My Classes" — so
// StaffApiInterceptor attaches the staff session's own auth/device headers
// instead of needing an admin-issued token.

export interface BroadcastMediaItem {
  broadcastMediaType: string;
  // sic — API typo, kept verbatim to match the response field name.
  broadcastMedisSubType: string;
  broadcastMediaUrl: string;
}

export interface BroadcastListItem {
  broadcastId: string;
  broadcastSubject: string;
  broadcastMessage: string;
  broadcastReceiver: 'PARENTS' | 'STAFFS' | 'ALL';
  isRead: boolean;
  views: number;
  expectedViews: number;
  readPercentage: number;
  sentOnDate: string;
  sentOnTime: string;
  broadcastMediaList: BroadcastMediaItem[];
}

export interface BroadcastStat {
  slug?: string;
  title: string;
  value: number | string;
}

export interface GetBroadcastMessagesForStaffRequest {
  academicId: number | string;
  receiverType: string | null;
  search: string | null;
  startDate: string | null;
  endDate: string | null;
  readStatus: 'UNREAD' | null;
  pageNumber: number;
  pageLimit: number;
}

export interface GetBroadcastMessagesForStaffResponse {
  status: boolean;
  message?: string;
  body?: {
    broadcastDetail: BroadcastListItem[];
    maxPageNumber: number;
    stats: BroadcastStat[];
    unreadCount: number;
  };
}

export interface BroadcastReceiverDetail {
  userId: string;
  name: string;
  image: string | null;
  type: string;
  className: string | null;
  readDate: string | null;
  readTime: string | null;
}

export interface GetBroadcastReceiverDetailListRequest {
  broadcastId: string;
  pageNumber: number;
  pageLimit: number;
  search: string | null;
}

export interface GetBroadcastReceiverDetailListResponse {
  status: boolean;
  message?: string;
  body?: {
    receiverDetailList: BroadcastReceiverDetail[];
    maxPageNumber: number;
  };
}

@Injectable({ providedIn: 'root' })
export class StaffBroadcastService {
  private url = AppSettings.API_ENDPOINT_STAFF;

  constructor(private http: HttpClient) {}

  // Per spec: GET /get/broadcast/receiver/list?academicId={academicId} —
  // populates both the Overview's Filter sheet and the Create sheet's
  // "Send To" picker.
  getBroadcastType(academicId: number | string) {
    const params = new HttpParams().set('academicId', String(academicId));
    return this.http
      .get(this.url + apiConstants.getBroadcastTypeAPI, { params })
      .pipe(map((res) => res));
  }

  createBroadcastService(body: any, media: File | File[] | null) {
    const form = new FormData();
    for (const key in body) {
      if (body.hasOwnProperty(key)) {
        form.append(key, body[key]);
      }
    }
    if (Array.isArray(media)) {
      media.forEach((file) => {
        if (file) {
          form.append('broadcastMedia', file, file.name);
        }
      });
    } else if (media) {
      form.append('broadcastMedia', media, media.name);
    } else {
      form.append('broadcastMedia', null);
    }
    return this.http
      .post(this.url + apiConstants.createBroadcastAPI, form)
      .pipe(map((res) => res));
  }

  getBroadcastMessagesForStaff(body: GetBroadcastMessagesForStaffRequest) {
    return this.http
      .post<GetBroadcastMessagesForStaffResponse>(this.url + apiConstants.getBroadcastMessagesForStaffAPI, body)
      .pipe(map((res) => res));
  }

  // Fire-and-forget by design in the mobile app's own implementation (see
  // spec: "no await, no error handling") — callers may still await this if
  // they want to reconcile, but nothing here requires it.
  markBroadcastReadForStaff(broadcastId: string) {
    return this.http
      .post(this.url + apiConstants.markBroadcastReadForStaffAPI, { broadcastId })
      .pipe(map((res) => res));
  }

  // Only ever called for Super Admins — the read-receipt breakdown behind
  // the Details sheet's "Who viewed and when" section.
  getBroadcastReceiverDetailList(body: GetBroadcastReceiverDetailListRequest) {
    return this.http
      .post<GetBroadcastReceiverDetailListResponse>(this.url + apiConstants.getBroadcastReceiverDetailListAPI, body)
      .pipe(map((res) => res));
  }
}
